// mapMeToDashboardSession — pure function that converts the
// /v1/auth/me response shape (defined in apps/auth-api/src/handlers/me.ts)
// into the DashboardSession shape consumed by the SPA.
//
// Kept in its own React-free module so the conversion can be unit-tested
// without needing JSDOM / React Testing Library.

import type { MeResponse } from "../../lib/auth.js";

export interface DashboardSessionShape {
  tenant_id: string;
  user_id: string | null;
  role: string;
  scopes: string[];
  /** Permission ids (Custom RBAC). May be empty for guests / unmapped roles. */
  permissions: string[];
  email?: string;
  display_name?: string;
  super_admin?: boolean;
  acting_tenant_id?: string;
  acting_tenant_name?: string;
}

/**
 * Map the /v1/auth/me response into the DashboardSessionProvider shape.
 *
 * Handles three cases the auth-api emits:
 *   - super_admin → role=super_admin, tenant_id=_platform, scopes full
 *   - tenant member → role=v2 (tenant_admin/manager/officer/auditor/...),
 *     tenant_id=membership.tenant_id
 *   - authenticated but unbound (membership_required=true) → role=guest,
 *     tenant_id="" — kept as guest so RequireRole still routes them to
 *     /forbidden, but user_id/email are populated so the UI can show
 *     "you are X but not yet a member of any tenant".
 */
export function mapMeToDashboardSession(me: MeResponse): DashboardSessionShape {
  return {
    tenant_id: me.tenant_id ?? "",
    user_id: me.user_id ?? null,
    role: me.role,
    scopes: Array.isArray(me.scopes) ? me.scopes : [],
    permissions: Array.isArray(me.permissions) ? me.permissions : [],
    email: me.email,
    display_name: me.display_name ?? undefined,
    super_admin: me.super_admin === true ? true : undefined,
    acting_tenant_id: me.acting_tenant_id ?? undefined,
    acting_tenant_name: me.acting_tenant_name ?? undefined,
  };
}
